export const CHART_COLORS = {
  quantum: '#39ff14',
  coverage: '#2F80ED',
  efficiency: '#FF9800',
  optimal: '#111827',
  random: '#9AA4B2',
  grid: '#E6E9EF',
  axis: '#7B8794',
  ink: '#0B1220',
  heatmap: {
    low: '#10B981',
    medium: '#F59E0B',
    high: '#EF4444',
  },
};

export type TrendPoint = {
  date: string;
  dateLabel: string;
  ts: number;
  risk_coverage: number;
  tests_executed_pct: number;
  efficiency_score: number;
  aggregatedCount?: number;
};

export type HeatmapCell = {
  service: string;
  run_id: string;
  run_number: number;
  rank: number;
  risk_percentage: number;
  risk_level: 'low' | 'medium' | 'high';
  color: string;
};

export type ComparatorBar = {
  strategy: string;
  testsPct: number;
  coveragePct: number;
  timeMinutes: number;
  color: string;
  source: 'observed' | 'estimate';
  sourceLabel: string;
};

export interface AnalyticsData {
  trendData: TrendPoint[];
  heatmapRuns: Array<{ run_id: string; run_number: number; created_at: string }>;
  heatmapData: HeatmapCell[];
  comparatorData: ComparatorBar[];
  efficiencyMultiplier: number;
  comparatorError?: string;
}

export const pct = (value: number, _decimals = 1) => formatPercentage(value);

export const formatDate = (dateValue: string | number | Date) => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

  return (
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }) +
    ' ' +
    date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  );
};

export const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel) {
    case 'low':
      return CHART_COLORS.heatmap.low;
    case 'medium':
      return CHART_COLORS.heatmap.medium;
    case 'high':
      return CHART_COLORS.heatmap.high;
    default:
      return CHART_COLORS.grid;
  }
};

export const mapTrendPoint = (point: any): TrendPoint => ({
  date: point.date,
  ts: new Date(point.date).getTime(),
  dateLabel: formatDate(point.date),
  risk_coverage: point.risk_coverage,
  tests_executed_pct: point.tests_executed_pct,
  efficiency_score: point.efficiency_score,
  aggregatedCount: point.run_count || 1,
});

export const mapHeatmapCell = (cell: any): HeatmapCell => ({
  ...cell,
  color: getRiskColor(cell.risk_level),
});

export type WhatIfComparisonPayload = {
  quantum_tests_pct: number;
  quantum_risk_coverage: number;
  quantum_time_minutes: number;
  random_tests_pct: number;
  random_risk_coverage: number;
  random_time_minutes: number;
  full_suite_tests_pct: number;
  full_suite_risk_coverage: number;
  full_suite_time_minutes: number;
  efficiency_multiplier: number;
  baseline_source?: string | null;
  baseline_source_label?: string | null;
};

export const hasUsableWhatIfComparison = (
  payload: WhatIfComparisonPayload | null | undefined
) =>
  Boolean(
    payload &&
      (payload.quantum_tests_pct > 0 ||
        payload.quantum_risk_coverage > 0 ||
        payload.quantum_time_minutes > 0)
  );

export const buildComparatorData = (
  payload: WhatIfComparisonPayload
): ComparatorBar[] => {
  return [
    {
      strategy: 'Quantik Mind',
      testsPct: payload.quantum_tests_pct,
      coveragePct: payload.quantum_risk_coverage,
      timeMinutes: payload.quantum_time_minutes,
      color: CHART_COLORS.quantum,
      source: payload.baseline_source === 'sample_generated_seed' ? 'estimate' : 'observed',
      sourceLabel:
        payload.baseline_source_label ??
        (payload.baseline_source === 'current_run'
          ? 'Current run'
          : payload.baseline_source === 'sample_generated_seed'
          ? 'Sample generated estimate'
          : 'Project average, last 30 days'),
    },
    {
      strategy: 'Random Selection\n(50%)',
      testsPct: payload.random_tests_pct,
      coveragePct: payload.random_risk_coverage,
      timeMinutes: payload.random_time_minutes,
      color: '#6B7280',
      source: 'estimate',
      sourceLabel: 'Illustrative estimate',
    },
    {
      strategy: 'Full Suite Reference',
      testsPct: payload.full_suite_tests_pct,
      coveragePct: payload.full_suite_risk_coverage,
      timeMinutes: payload.full_suite_time_minutes,
      color: '#374151',
      source: 'estimate',
      sourceLabel: 'Illustrative estimate',
    },
  ];
};

export const calculateEfficiencyMultiplier = (
  riskCoverage: number,
  comparatorData: ComparatorBar[]
) => {
  const quantumTestsPct = comparatorData[0]?.testsPct ?? 0;
  return quantumTestsPct > 0 ? riskCoverage / quantumTestsPct : 1.0;
};

export const CARBON_CONSTANTS = {
  AVG_CPU_WATT_HOUR: 12.5,
  AVG_PUE: 1.11,
  INTENSITY_GLOBAL: 475,
  INTENSITY_EU: 295,
  INTENSITY_US: 385,
  TREE_ABSORPTION_KG_YEAR: 21,
  CAR_EMISSION_KG_KM: 0.12,
  FLIGHT_SHORT_HAUL_KG: 250,
  HOME_HEATING_MONTH_KG: 540,
};

export const kgToTonnes = (kg: number) => kg / 1000;

export const calculateObservedRunsPerDay = (
  totalRuns?: number | null,
  daysActive?: number | null
) => {
  if (
    typeof totalRuns !== 'number' ||
    typeof daysActive !== 'number' ||
    totalRuns <= 0 ||
    daysActive <= 0
  ) {
    return null;
  }

  return totalRuns / daysActive;
};

export const calculateAnnualCarbonProjectionKg = (
  kgSavedPerRun?: number | null,
  observedRunsPerDay?: number | null
) => {
  if (
    typeof kgSavedPerRun !== 'number' ||
    !Number.isFinite(kgSavedPerRun) ||
    kgSavedPerRun <= 0 ||
    typeof observedRunsPerDay !== 'number' ||
    observedRunsPerDay <= 0
  ) {
    return null;
  }

  return kgSavedPerRun * observedRunsPerDay * 365;
};

export function calculateCarbonImpact(
  testTimeSavedMinutes: number,
  estimatedCPUs: number = 4,
  region: 'global' | 'eu' | 'us' = 'global'
) {
  const cpuHoursSaved = (testTimeSavedMinutes / 60) * estimatedCPUs;

  const energySavedKWh =
    (cpuHoursSaved *
      CARBON_CONSTANTS.AVG_CPU_WATT_HOUR *
      CARBON_CONSTANTS.AVG_PUE) /
    1000;

  const intensityMap = {
    global: CARBON_CONSTANTS.INTENSITY_GLOBAL,
    eu: CARBON_CONSTANTS.INTENSITY_EU,
    us: CARBON_CONSTANTS.INTENSITY_US,
  };

  const carbonIntensity = intensityMap[region];
  const co2SavedKg = (energySavedKWh * carbonIntensity) / 1000;

  return {
    raw: {
      cpuHoursSaved: cpuHoursSaved.toFixed(1),
      energySavedKWh: energySavedKWh.toFixed(2),
      co2SavedKg: co2SavedKg.toFixed(2),
      co2SavedTonnes: (co2SavedKg / 1000).toFixed(4),
    },
    equivalents: {
      trees: co2SavedKg / CARBON_CONSTANTS.TREE_ABSORPTION_KG_YEAR,
      carKm: co2SavedKg / CARBON_CONSTANTS.CAR_EMISSION_KG_KM,
      flights: co2SavedKg / CARBON_CONSTANTS.FLIGHT_SHORT_HAUL_KG,
      homeHeatingDays:
        (co2SavedKg / CARBON_CONSTANTS.HOME_HEATING_MONTH_KG) * 30,
    },
    perRun: co2SavedKg,
  };
}
import { formatPercentage } from '../../../utils/percentage';
